import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import {
  Package,
  Plus,
  BarChart3,
  Users,
  Settings,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@features/auth/contexts/AuthContext';
import PickupDashboardWidget from "@/features/pickups/components/PickupDashboardWidget";
import PickupStatusTracking from "@/features/pickups/components/PickupStatusTracking";
import PickupAssignmentSummary from "@/features/pickups/components/PickupAssignmentSummary";
import PickupOverviewWidget from "@/features/pickups/components/PickupOverviewWidget";
import LivreurPickupManagementWidget from "@/features/pickups/components/LivreurPickupManagementWidget";
import HeadLayout from "@shared/layouts/HeadLayout";

const PickupDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLivreur = user?.role === "livreur";

  const tabs = [
    {
      id: "overview",
      label: t("Overview"),
      icon: Package,
      show: true,
    },
    {
      id: "analytics",
      label: t("Analytics"),
      icon: BarChart3,
      show: true,
    },
    {
      id: "assignments",
      label: t("Assignments"),
      icon: Users,
      show: isAdmin || isManager,
    },
    {
      id: "management",
      label: t("Management"),
      icon: Settings,
      show: true,
    },
  ];

  return (
    <div className="space-y-6">
      <HeadLayout
        title={isLivreur ? "My Pickup Dashboard" : "Pickup Dashboard"}
      >
        <div className="flex items-center gap-2">
          {(isAdmin || isManager) && (
            <Button onClick={() => navigate("/pickups/create")}>
              <Plus className="h-4 w-4 mr-2" />
              {t("Create Pickup")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/pickups/management")}
          >
            <Settings className="h-4 w-4 mr-2" />
            {t("Manage Pickups")}
          </Button>
        </div>
      </HeadLayout>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-4">
          {tabs
            .filter((tab) => tab.show)
            .map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2"
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            ))}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PickupOverviewWidget />
            {isLivreur ? (
              <LivreurPickupManagementWidget />
            ) : (isAdmin || isManager) ? (
              <PickupAssignmentSummary />
            ) : (
              <PickupOverviewWidget />
            )}
          </div>

          {/* Quick Stats */}
          <PickupDashboardWidget />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <PickupStatusTracking />
        </TabsContent>

        {/* Assignments Tab (Admin/Manager Only) */}
        {(isAdmin || isManager) && (
          <TabsContent value="assignments" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PickupAssignmentSummary />
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    {t("Assignment Metrics")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        {t("Assignment Analytics")}
                      </h3>
                      <p className="text-muted-foreground">
                        {t("Detailed assignment metrics coming soon")}
                      </p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() =>
                          navigate("/packages/admin/livreur-management")
                        }
                      >
                        {t("Manage Assignments")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Management Tab */}
        <TabsContent value="management" className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("Pickup Management Tools")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto p-6 flex flex-col items-center gap-3"
                    onClick={() => navigate("/pickups")}
                  >
                    <Package className="h-8 w-8" />
                    <div className="text-center">
                      <div className="font-semibold">{t("All Pickups")}</div>
                      <div className="text-sm text-muted-foreground">
                        {t("View and manage all pickups")}
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-auto p-6 flex flex-col items-center gap-3"
                    onClick={() => navigate("/pickups/management")}
                  >
                    <Settings className="h-8 w-8" />
                    <div className="text-center">
                      <div className="font-semibold">
                        {t("Management Dashboard")}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {t("Advanced management tools")}
                      </div>
                    </div>
                  </Button>

                  {(isAdmin || isManager) && (
                    <Button
                      variant="outline"
                      className="h-auto p-6 flex flex-col items-center gap-3"
                      onClick={() =>
                        navigate("/packages/admin/livreur-management")
                      }
                    >
                      <Users className="h-8 w-8" />
                      <div className="text-center">
                        <div className="font-semibold">
                          {t("Livreur Management")}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t("Manage assignments and livreurs")}
                        </div>
                      </div>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Detailed Management */}
            <PickupDashboardWidget />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PickupDashboardPage;
